import * as React from 'react';
import { createEvent, historyEvent, isIOS, scrollPosition, saveScrollPosition } from '@avensia/excite';

let shouldRestoreScrollPosition = true;
let currentScrollbarSize = 0;

type FreezeScrollEvent = {
  freeze: boolean;
};

export const [freezeScrollEvent] = createEvent<FreezeScrollEvent>();
function feezeScroll(currentScrollPosition: number) {
  const element = document.body;
  const currentStyle = element.style.cssText;
  if (isIOS()) {
    currentScrollbarSize = 0;
    shouldRestoreScrollPosition = true;
    // Setting position fixed is the only way to prevent body scroll
    // to occur on iOS when you reach the bottom of the scroll position in
    // a floating layer
    Object.assign(element.style, {
      // A rendering bug occurs in iOS10.3 when nesting fixed positioned
      // elements. The properties below creates a temporarily workaround,
      // discovered in https://bugs.webkit.org/show_bug.cgi?id=170280#c6,
      // until the fix is released and the majority of users has updated
      // their devices.
      margin: '-1px',
      padding: '1px',
    });

    let abort = false;

    requestAnimationFrame(() => {
      // Abort if reset function is triggered on the same tick
      if (abort) {
        return;
      }

      // Setting position fixed is the only way to prevent body scroll
      // to occur on iOS when you reach the bottom of the scroll position in
      // a floating layer
      Object.assign(element.style, {
        position: 'fixed',
        left: '0',
        right: '0',
        top: currentScrollPosition * -1 + 'px',
      });
    });

    return () => {
      abort = true;
      element.style.cssText = currentStyle;
      if (shouldRestoreScrollPosition) {
        scrollPosition(currentScrollPosition);
      }
    };
  } else {
    const scrollbarWidth = getScrollbarWidth();
    if (currentScrollbarSize === 0) {
      // Store the current size of the scrollbar before setting overflow hidden
      currentScrollbarSize = scrollbarWidth;
    }
    element.style.overflow = 'hidden';
    element.style.paddingRight = scrollbarWidth + 'px';

    return () => {
      element.style.cssText = currentStyle;
      currentScrollbarSize = 0;
    };
  }
}

let enableScroll: (() => void) | undefined;
export function useFreezeScroll(freeze: boolean) {
  const previousFreeze = React.useRef(freeze);
  const scrollPos = React.useRef<number>(undefined);
  React.useEffect(() => {
    if (freeze && enableScroll === undefined) {
      scrollPos.current = scrollPosition();
      enableScroll = feezeScroll(scrollPos.current);
    } else if (!freeze) {
      enableScroll?.();
      enableScroll = undefined;
    }

    return historyEvent.on(event => {
      if (scrollPos.current && previousFreeze.current) {
        shouldRestoreScrollPosition = false;
        saveScrollPosition(event.previousState, scrollPos.current);
      }
    });
  }, [freeze]);

  React.useEffect(() => {
    previousFreeze.current = freeze;
  }, [freeze]);

  React.useEffect(
    () => () => {
      enableScroll?.();
      enableScroll = undefined;
    },
    [],
  );
}

export function getScrollbarWidth(): number {
  if (__BROWSER__) {
    if (isIOS()) {
      return 0;
    } else if (currentScrollbarSize > 0) {
      return currentScrollbarSize;
    }
    return window.innerWidth - document.body.clientWidth;
  }
  return 0;
}
